import React, { type FC, useEffect, useState } from 'react';
import moment, { type Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';

import { Spinner } from 'components/Shared';
import { Card } from 'components/Shared/Card';
import { Button } from 'components/Shared/Inputs';
import { DateInput } from 'components/Shared/Inputs/DateInput';
import { useFetchAverageCompletedSessions } from 'api/queries/credentialsQuery';
import { formatIntegerOrFloat } from 'utils';


import './AverageCompletedSessions.scss';

interface AverageCompletedSessionsProps {
    providerId: number;
    loading: boolean;
}

export const AverageCompletedSessions: FC<AverageCompletedSessionsProps> = (props) => {
    const { providerId, loading } = props;
    const [defaultStartDate, defaultEndDate, aYearAgo] = Object.freeze([
        moment().endOf('day').subtract(7, 'days'), 
        moment().endOf('day').subtract(1, 'day'),
        moment().endOf('day').subtract(1, 'year')
    ]);
    const [startDate, setStartDate] = useState<Moment|null>(defaultStartDate);
    const [endDate, setEndDate] = useState<Moment|null>(defaultEndDate);
    const { t } = useTranslation('common', { useSuspense: false });
    const [fromDate, toDate] = [startDate?.toISOString()?.substring(0,10), endDate?.toISOString()?.substring(0, 10)];

    const { data: averageSessions = '0', isFetching, refetch } = useFetchAverageCompletedSessions({
        providerId,
        fromDate: fromDate!,
        toDate: toDate!
    });

    const handleReset = () => {
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
    }

    useEffect(() => {
        refetch();
    }, [fromDate, toDate]);

    return (
        <Card className="average_sessions_container" elevation={1} style={{ minHeight: '200px' }}>
            {
                loading
                ? <Spinner />
                :   <div className="average_sessions_card">
                        <div className="average_sessions_summary">
                            <div className="averge_sessions_text">{isFetching ? <CircularProgress size="24px" sx={{ color: "currentcolor", m: "5px" }} /> : isNaN(parseFloat(averageSessions)) ? <span style={{ padding: '7px' }}>&#x20E0;</span> : <span className="average_sessions_value">{formatIntegerOrFloat(parseFloat(averageSessions))}</span>}/{t('day')}</div>
                            <div><Button variant='text' onClick={handleReset}>{t('reset')}</Button></div>
                        </div>
                        <div className='average_sessions_date_range'>
                            <FormControl style={{ margin: "8px 5px" }} fullWidth={false}>
                                <DateInput
                                    label={t('from')}
                                    value={startDate}
                                    onChange={setStartDate}
                                    minDate={aYearAgo}
                                    maxDate={endDate ?? defaultEndDate}
                                    slotProps={{
                                        textField: { size: 'small' }
                                    }}
                                    disableKeyboardInput
                                />
                            </FormControl>
                            <FormControl style={{ margin: "8px 5px" }} fullWidth={false}>
                                <DateInput
                                    label={t('to')}
                                    value={endDate}
                                    onChange={setEndDate}
                                    minDate={startDate ?? aYearAgo}
                                    maxDate={defaultEndDate}
                                    slotProps={{
                                        textField: { size: 'small' }
                                    }}
                                    disableKeyboardInput
                                />
                            </FormControl>
                        </div>
                    </div>
            }
        </Card>
    )
}
